import { Skeleton, SkeletonCircle } from '@chakra-ui/react';
import { Song } from 'types/song';
import { AudioStatusType, AudioTypeOption, AudioTypesDDL } from 'types/audio';
import {
  AudioSelectionItem,
  AudioValidationErrorCodes,
  SelectedAudioObject,
} from './types';

export const AUDIO_SELECTION_LIST_ID = 'audio-selection-list';
export const AUDIO_SELECTION_LIST_CONTAINER_ID =
  'audio-selection-list-container';
export const DEFAULT_VERSION_TYPE = 'SingleVersion';

export const getAudioListFromSongs = (songs: Song[]): AudioSelectionItem[] =>
  songs
    .map((song) =>
      (song.audio || [])?.map((item) => ({
        ...item,
        songDetails: {
          status: song.status,
          title: song.title,
        },
        isLatestVersion: song.latestVersionAudio === item.guid,
      }))
    )
    .flat(1);

export const isAudioListScrollbarVisibile = () => {
  const listElement = document.getElementById(AUDIO_SELECTION_LIST_ID)
    ?.firstElementChild;
  const listContainerElement = document.getElementById(
    AUDIO_SELECTION_LIST_CONTAINER_ID
  );
  return (
    listElement &&
    listContainerElement &&
    listElement?.scrollHeight > listContainerElement?.clientHeight
  );
};

export const getAudioTypes = (types: AudioTypesDDL, keys: string[]) => {
  const allTypes = Object.values(types).map(({ key }) => key);
  if (!keys.length) {
    return allTypes;
  }

  return keys.filter((key) => allTypes.includes(key));
};

export const getRowState = ({
  checked,
  audio,
  maxAudioCount,
}: {
  checked: SelectedAudioObject;
  audio: AudioSelectionItem;
  maxAudioCount?: number;
}) => {
  const checkedFilesCount = Object.keys(checked).length;
  const isChecked = Boolean(checked[audio.guid]);
  const disableNext = maxAudioCount
    ? !isChecked && checkedFilesCount >= maxAudioCount
    : false;
  const isLoading = ({ isAudioLoading, audioPlayerGuid }) =>
    isAudioLoading && audio.guid === audioPlayerGuid;
  const isError = audio.status === AudioStatusType.FAILURE;
  const isValid = audio.validationResult
    ? audio.validationResult.isValid
    : true;

  return { isChecked, disableNext, isLoading, isError, isValid };
};

export const addAudioToChecked = (
  checked: SelectedAudioObject,
  audio: AudioSelectionItem,
  labelV2ReleaseEnabled: boolean
) => {
  return {
    ...checked,
    [audio.guid]: {
      audioGuid: audio.guid,
      songGuid: audio.parent,
      audioTitle: audio.title,
      songTitle: audio.songDetails.title,
      type: labelV2ReleaseEnabled ? DEFAULT_VERSION_TYPE : 'AlbumVersion',
    },
  };
};

export const audioSelectListColumns = [
  {
    key: 'controls',
    name: '',
    accessory: true,
    width: 32,
    minWidth: 32,
  },
  { key: 'title', name: 'Title' },
  { key: 'song', name: 'Song' },
  {
    key: 'date',
    name: 'Date',
  },
  { key: 'audioType', name: 'Type' },
  {
    key: 'checkbox',
    name: '',
    contextMenu: true,
    minWidth: 60,
  },
];

export const getInvalidAudioErrorMessage = (
  errorCode: string,
  vendor = 'This vendor'
) => {
  let errorMessage: string;
  switch (errorCode) {
    case AudioValidationErrorCodes.CONTAINER_IS_NOT_SUPPORTED:
      errorMessage = `${vendor} only accepts audio files of type WAV, FLAC, MP3. Please select another one.`;
      break;
    case AudioValidationErrorCodes.DURATION_IS_TOO_SMALL:
      errorMessage = `${vendor} only accepts audio files that have at least 2 seconds duration. Please select another one`;
      break;
    case AudioValidationErrorCodes.BIT_RATE_IS_TOO_LOW:
    case AudioValidationErrorCodes.BITS_PER_SAMPLE_IS_TOO_LOW:
    case AudioValidationErrorCodes.SAMPLE_RATE_IS_TOO_LOW:
      errorMessage = `${vendor} only accepts audio files that are at least 16 bit and 44.1 khz or higher. Please select another one.`;
      break;
    case AudioValidationErrorCodes.CHANNEL_COUNT_IS_NOT_SUPPORTED:
      errorMessage = `${vendor} only accepts audio files that has 1 or 2 channels. Please select another one.`;
      break;
    default:
      errorMessage =
        'Audio file can not be accepted. Please select another one.';
  }
  return errorMessage;
};

export const getAudioTypesFilters = (
  audioTypes: AudioTypesDDL
): AudioTypeOption[] => {
  return Object.values(audioTypes);
};

export const getListLoadingOptions = (
  loading?: boolean,
  loadingMore?: boolean
) => ({
  loading: Boolean(loading),
  loadingMore: Boolean(loadingMore),
  repeat: 3,
  loader: {
    controls: (
      <SkeletonCircle
        width="20px"
        height="20px"
        data-testid="controls-loading-skeleton"
      />
    ),
    title: <Skeleton width="100%" height="24px" />,
    song: <Skeleton width="100%" height="24px" />,
    date: <Skeleton width="100%" height="24px" />,
    audioType: <Skeleton width="100%" height="24px" />,
  },
});
